// ---------------------------------------------------------------------------
// Choosing a password after an organizer issued one -- at sign-up, or after a
// reset.
//
// The temporary password was shown to somebody else, so it is not allowed to
// stay. The shell renders this instead of everything else until the database
// agrees it has changed (password_change_required(), 20260927000100). Signing
// out is offered for a shared phone; it does not skip anything, since the next
// sign-in with the temporary password lands back here.
// ---------------------------------------------------------------------------

import { useState, type FormEvent } from 'react';
import { MIN_PASSWORD_LENGTH, passwordChangeSchema } from '@yucanvas/shared';
import { useAuth } from '../auth/AuthProvider';

export function ChangePasswordScreen() {
  const { profile, changePassword, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = passwordChangeSchema.safeParse({ password, confirm });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check your password.');
      return;
    }

    setBusy(true);
    try {
      // On success mustChangePassword goes false and the shell swaps this
      // screen out on its own.
      await changePassword(parsed.data.password);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <h1 className="login__title">Choose a password</h1>
      <p className="login__sub">
        {profile ? `Hi, ${profile.name}.` : 'Hi.'} The password you signed in
        with came from an organizer. Pick one only you know.
      </p>

      <form className="login__form" onSubmit={submit}>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="field__hint">
            At least {MIN_PASSWORD_LENGTH} characters, and not the one you were
            just given. Nobody else will see this one.
          </span>
        </label>

        <label className="field">
          <span>Type it again</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        {error ? <p className="banner banner--error">{error}</p> : null}

        <button className="btn btn--primary btn--huge" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save my password'}
        </button>
      </form>

      <p className="login__note">
        Not you?{' '}
        <button
          className="btn btn--link"
          type="button"
          onClick={() =>
            void signOut().catch((cause: unknown) =>
              setError(cause instanceof Error ? cause.message : 'Could not sign out.'),
            )
          }
        >
          Sign out
        </button>
      </p>
    </main>
  );
}
